import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './CitySearch.css';
import { Search } from 'lucide-react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

/**
 * Props:
 * - city, setCity           -> parent "confirmed" city (we only set on selection or prefill)
 * - displayText             -> parent-managed formatted text shown in the input
 * - onLocation(payload)     -> { type, city, state, countryCode, countryName, venueName, display, lat, lng, selected }
 * - onDisplay(text)         -> raw text the user sees/types (and prefilled)
 * - inputId                 -> optional custom id for the input
 * - placeholder             -> optional custom placeholder text
 * - searchScope             -> 'all' | 'city'
 */
const CitySearch = ({
  city,
  displayText = '',
  setCity,
  onLocation,
  onDisplay,
  inputId = 'city-input',
  placeholder = 'Search city or venue',
  persistToLocalStorage = true,
  searchScope = 'all',
}) => {
  const places = useMapsLibrary('places');
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);
  const listenerRef = useRef(null);
  const selectedLocationRef = useRef(null);
  const venueTypes = useRef(
    new Set(['establishment', 'point_of_interest', 'stadium', 'premise', 'tourist_attraction', 'park'])
  );
  const cityTypes = useRef(
    new Set(['locality', 'postal_town', 'administrative_area_level_2', 'administrative_area_level_3'])
  );

  // helper: parse "City, ST[, Country]" into pieces
  const parseCityLine = (s) => {
    if (!s) return null;
    const m = String(s).trim().match(/^\s*(.+?),\s*([A-Za-z]{2})(?:\s*,\s*([A-Za-z\s]+))?\s*$/);
    if (!m) return null;
    const cityName = m[1].trim();
    const stateAbbr = m[2].toUpperCase();
    const countryName = (m[3]?.trim()) || 'United States';
    const countryCode = countryName === 'United States' ? 'US' : null;
    return { cityName, stateAbbr, countryName, countryCode };
  };

  const parseFormattedAddress = (formatted) => {
    if (!formatted) return null;

    const [first = '', second = '', third = ''] = String(formatted)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);

    return {
      cityName: first || '',
      stateAbbr: second || null,
      countryName: third || null,
    };
  };

  const shouldRestoreSelectedDisplay = (value, selectedDisplay) => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    const normalizedSelected = String(selectedDisplay || '').trim().toLowerCase();

    if (!normalizedSelected) return false;
    if (!normalizedValue) return true;

    return (
      normalizedSelected.startsWith(normalizedValue) &&
      normalizedSelected.length > normalizedValue.length
    );
  };

  useEffect(() => {
    if (!persistToLocalStorage) {
      const nextDisplay = String(displayText || '').trim();
      const nextCity = String(city || '').trim();

      if (!nextDisplay && !nextCity) {
        setDisplayValue('');
        onDisplay?.('');
        return;
      }

      const resolvedDisplay = nextDisplay || nextCity;
      setDisplayValue(resolvedDisplay);
      onDisplay?.(resolvedDisplay);
    }
  }, [city, displayText, onDisplay, persistToLocalStorage]);

  useLayoutEffect(() => {
    if (persistToLocalStorage) return;
    if (!inputRef.current) return;
    if (document.activeElement === inputRef.current) return;

    const selectedDisplay = selectedLocationRef.current?.display || '';
    const domValue = inputRef.current.value || '';
    const externalDisplay = String(displayText || '').trim();
    const fallbackDisplay = externalDisplay || selectedDisplay || String(city || '').trim();

    if (!fallbackDisplay) return;

    if (
      shouldRestoreSelectedDisplay(domValue, selectedDisplay) ||
      shouldRestoreSelectedDisplay(domValue, fallbackDisplay)
    ) {
      inputRef.current.value = fallbackDisplay;
      if (displayValue !== fallbackDisplay) {
        setDisplayValue(fallbackDisplay);
      }
      onDisplay?.(fallbackDisplay);
    }
  }, [city, displayText, displayValue, onDisplay, persistToLocalStorage]);

  const inputValue = persistToLocalStorage
    ? displayValue
    : (String(displayText || '').trim() || String(city || '').trim());

  useEffect(() => {
    if (persistToLocalStorage) {
      const storedDisplay = localStorage.getItem('displayCity');
      const storedCity = localStorage.getItem('selectedCity');
      const storedKind = localStorage.getItem('searchKind');
      const storedVenueName = localStorage.getItem('selectedVenueName');
      const storedVenueLat = localStorage.getItem('selectedVenueLat');
      const storedVenueLng = localStorage.getItem('selectedVenueLng');
      const storedVenueCity = localStorage.getItem('selectedVenueCity');
      const storedVenueState = localStorage.getItem('selectedVenueState');
      const storedVenueCountry = localStorage.getItem('selectedVenueCountry');

      if (storedDisplay) {
        setDisplayValue(storedDisplay);
        onDisplay?.(storedDisplay);
        if (storedKind === 'venue' && storedVenueName) {
          const lat = storedVenueLat ? Number(storedVenueLat) : null;
          const lng = storedVenueLng ? Number(storedVenueLng) : null;
          onLocation?.({
            type: 'venue',
            city: storedVenueCity || null,
            state: storedVenueState || null,
            countryCode: storedVenueCountry === 'United States' ? 'US' : null,
            countryName: storedVenueCountry || null,
            venueName: storedVenueName,
            display: storedDisplay,
            lat: Number.isFinite(lat) ? lat : null,
            lng: Number.isFinite(lng) ? lng : null,
            selected: false,
          });
        } else {
          const parsed = parseCityLine(storedDisplay);
          if (parsed) {
            onLocation?.({
              type: 'city',
              city: parsed.cityName,
              state: parsed.stateAbbr,
              countryCode: parsed.countryCode,
              countryName: parsed.countryName,
              display: storedDisplay,
              selected: false,
            });
          }
        }
      }
      if (storedCity) {
        setCity?.(storedCity);
      }
    }

    const el = document.getElementById(inputId);
    if (!el || !places) {
      return;
    }

    const options = {
      fields: ['address_components', 'geometry', 'formatted_address', 'name', 'types'],
    };

    if (searchScope === 'city') {
      options.types = ['(cities)'];
    }

    const ac = new places.Autocomplete(el, options);

    const onPlaceChanged = () => {
      const place = ac.getPlace?.();
      if (!place) return;

      const comps = place.address_components || [];
      const getComp = (t) => comps.find((c) => c.types.includes(t));

      const cityComp =
        getComp('locality') ||
        getComp('postal_town') ||
        getComp('administrative_area_level_2');

      const stateComp = getComp('administrative_area_level_1');
      const countryComp = getComp('country');

      const types = place.types || [];
      const isVenue = types.some((t) => venueTypes.current.has(t));
      const isCity = types.some((t) => cityTypes.current.has(t));
      const searchType = searchScope === 'city'
        ? 'city'
        : isVenue && !isCity ? 'venue' : isVenue ? 'venue' : 'city';
      const parsedAddress = parseFormattedAddress(place.formatted_address);
      const formattedCityName = parsedAddress?.cityName || '';
      const placeName = place.name || '';
      const cityName = searchType === 'city'
        ? formattedCityName || cityComp?.long_name || placeName
        : cityComp?.long_name ||
          (formattedCityName.length > placeName.length && formattedCityName.startsWith(placeName)
            ? formattedCityName
            : placeName || formattedCityName);
      const stateAbbr = stateComp?.short_name || parsedAddress?.stateAbbr || null;
      const countryCode = countryComp?.short_name || (parsedAddress?.countryName === 'USA' ? 'US' : null);
      const countryName =
        (countryComp?.short_name === 'US' ? 'USA' : null) ||
        countryComp?.long_name ||
        parsedAddress?.countryName ||
        null;
      const cityLabel = [cityName, stateAbbr].filter(Boolean).join(', ');
      const formatted = searchType === 'venue'
        ? `${place.name}${cityLabel ? ` • ${cityLabel}` : ''}`
        : place.formatted_address || cityLabel || cityName;

      const location = place.geometry?.location;
      const lat = typeof location?.lat === 'function' ? location.lat() : location?.lat;
      const lng = typeof location?.lng === 'function' ? location.lng() : location?.lng;

      selectedLocationRef.current = {
        display: formatted,
        city: cityName || null,
        state: stateAbbr,
        countryCode,
        countryName,
      };

      setDisplayValue(formatted);
      if (persistToLocalStorage) {
        localStorage.setItem('displayCity', formatted);
        localStorage.setItem('searchKind', searchType);
      }

      if (searchType === 'city') {
        setCity?.(cityName);
        if (persistToLocalStorage) {
          localStorage.setItem('selectedCity', cityName);
          localStorage.removeItem('selectedVenueName');
          localStorage.removeItem('selectedVenueLat');
          localStorage.removeItem('selectedVenueLng');
          localStorage.removeItem('selectedVenueCity');
          localStorage.removeItem('selectedVenueState');
          localStorage.removeItem('selectedVenueCountry');
        }
      } else {
        if (cityName) {
          setCity?.(cityName);
        }
        if (persistToLocalStorage) {
          localStorage.setItem('selectedVenueName', place.name || formatted);
          if (Number.isFinite(lat)) localStorage.setItem('selectedVenueLat', String(lat));
          if (Number.isFinite(lng)) localStorage.setItem('selectedVenueLng', String(lng));
          if (cityName) localStorage.setItem('selectedVenueCity', cityName);
          if (stateAbbr) localStorage.setItem('selectedVenueState', stateAbbr);
          if (countryName) localStorage.setItem('selectedVenueCountry', countryName);
        }
      }

      onLocation?.({
        type: searchType,
        city: cityName || null,
        state: stateAbbr,
        countryCode,
        countryName,
        venueName: searchType === 'venue' ? (place.name || formatted) : null,
        lat: Number.isFinite(lat) ? lat : null,
        lng: Number.isFinite(lng) ? lng : null,
        display: formatted,
        selected: true,
      });

      onDisplay?.(formatted);
    };

    listenerRef.current = ac.addListener('place_changed', onPlaceChanged);

    return () => {
      if (listenerRef.current?.remove) listenerRef.current.remove();
    };
  }, [inputId, onDisplay, onLocation, persistToLocalStorage, places, searchScope, setCity]);

  return (
    <div className="city-search-container">
      <Search className="search-icon" size={20} aria-hidden="true" />
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => {
          if (document.activeElement !== inputRef.current) {
            return;
          }

          const typedValue = e.target.value;
          const selectedLocation = selectedLocationRef.current;

          if (
            selectedLocation?.display &&
            typedValue &&
            selectedLocation.display.toLowerCase().startsWith(typedValue.toLowerCase()) &&
            typedValue.length < selectedLocation.display.length
          ) {
            setDisplayValue(selectedLocation.display);
            onDisplay?.(selectedLocation.display);
            onLocation?.({
              city: selectedLocation.city,
              state: selectedLocation.state,
              countryCode: selectedLocation.countryCode,
              countryName: selectedLocation.countryName,
              selected: true,
            });
            return;
          }

          selectedLocationRef.current = null;
          setDisplayValue(typedValue);
          if (persistToLocalStorage) {
            localStorage.setItem('displayCity', typedValue);
          }
          onDisplay?.(typedValue);
          onLocation?.({ selected: false, type: null });
        }}
        onFocus={(e) => e.target.select()}
        onBlur={() => {
          const selectedDisplay = selectedLocationRef.current?.display || '';
          if (!selectedDisplay) return;

          if (shouldRestoreSelectedDisplay(inputRef.current?.value || '', selectedDisplay)) {
            setDisplayValue(selectedDisplay);
            onDisplay?.(selectedDisplay);
          }
        }}
        placeholder={placeholder}
        className="city-input"
        autoComplete="off"
        inputMode="text"
      />
    </div>
  );
};

export default CitySearch;
